/**
 * StudioDataPanel — LEFT sidebar.
 *
 * Two-mode panel:
 *  1. Default: universe / project picker + stats roll-up (counts, score
 *     summary, archived state).
 *  2. When a block is active: detail view (title, status, schedule, output
 *     intent, sections, cards).
 *
 * Click-through:
 *  - clicking a universe sets it active (filters projects)
 *  - clicking a project sets it active (filters blocks)
 *  - clicking a block surfaces the detail view here
 */

import { useEffect, useState } from 'react'
import { Inbox, FolderClosed, Sparkles, X } from 'lucide-react'
import { useStudioStore } from '@/stores/useStudioStore'
import {
  getBlock, listBlockSections, listBlockCards,
  type StudioBlock, type StudioBlockSection, type StudioCard,
} from '@/lib/studio'
import { format } from 'date-fns'

export function StudioDataPanel() {
  const universes = useStudioStore((s) => s.universes)
  const projects = useStudioStore((s) => s.projects)
  const blocks = useStudioStore((s) => s.blocks)
  const activeUniverseId = useStudioStore((s) => s.activeUniverseId)
  const activeProjectId = useStudioStore((s) => s.activeProjectId)
  const activeBlockId = useStudioStore((s) => s.activeBlockId)
  const setActiveUniverse = useStudioStore((s) => s.setActiveUniverse)
  const setActiveProject = useStudioStore((s) => s.setActiveProject)
  const setActiveBlock = useStudioStore((s) => s.setActiveBlock)

  if (activeBlockId) {
    return <BlockDetailPanel blockId={activeBlockId} onClose={() => setActiveBlock(null)} />
  }

  const filteredProjects = activeUniverseId
    ? projects.filter((p) => p.universe_id === activeUniverseId)
    : projects

  // Block stats — derived from the cached `blocks` list (the current
  // calendar window). Lightweight; for vault-wide stats we'd hit a
  // dedicated endpoint later.
  const stats = computeStats(blocks)

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-neutral-800 flex items-center gap-2">
        <Inbox className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-sm font-light text-neutral-200">Studio</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Universes */}
        <section className="px-3 py-3 border-b border-neutral-800">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-2 px-1">
            Universes
          </div>
          <div className="space-y-px">
            <Row
              label="All"
              active={activeUniverseId === null}
              onClick={() => setActiveUniverse(null)}
            />
            {universes.map((u) => (
              <Row
                key={u.id}
                label={u.name}
                color={u.color}
                active={activeUniverseId === u.id}
                onClick={() => setActiveUniverse(u.id)}
              />
            ))}
            {universes.length === 0 && (
              <div className="text-xs text-neutral-500 px-2 py-2">
                No universes yet. Ask the agent to create one →
              </div>
            )}
          </div>
        </section>

        {/* Projects */}
        <section className="px-3 py-3 border-b border-neutral-800">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-2 px-1">
            Projects {activeUniverseId ? `(${filteredProjects.length})` : ''}
          </div>
          <div className="space-y-px">
            <Row
              label="All projects"
              icon={<FolderClosed className="w-3.5 h-3.5" strokeWidth={1.5} />}
              active={activeProjectId === null}
              onClick={() => setActiveProject(null)}
            />
            {filteredProjects.map((p) => (
              <Row
                key={p.id}
                label={p.name}
                icon={<FolderClosed className="w-3.5 h-3.5" strokeWidth={1.5} />}
                active={activeProjectId === p.id}
                onClick={() => setActiveProject(p.id)}
              />
            ))}
            {filteredProjects.length === 0 && (
              <div className="text-xs text-neutral-500 px-2 py-2">
                {activeUniverseId ? 'No projects in this universe.' : 'No projects yet.'}
              </div>
            )}
          </div>
        </section>

        {/* Stats — this-window roll-up */}
        <section className="px-3 py-3">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-2 px-1">
            This window
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="blocks" value={stats.total} />
            <Stat label="completed" value={stats.completed} />
            <Stat label="scheduled" value={stats.scheduled} />
            <Stat label="pending" value={stats.pending} />
            <Stat label="overdue" value={stats.overdue} accent="text-red-400" />
            <Stat label="needs score" value={stats.scoreDue} accent="text-orange-400" />
          </div>
        </section>

        {/* Score summary */}
        <section className="px-3 py-3 border-t border-neutral-800">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-2 px-1">
            Score (this window)
          </div>
          <div className="flex items-center gap-3 px-1">
            <Sparkles className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
            <div className="text-sm text-neutral-200 font-light">
              {stats.scoredCount > 0
                ? `${Math.round((stats.scoredOnes / stats.scoredCount) * 100)}%`
                : '—'}
            </div>
            <div className="text-[10px] text-neutral-500">
              {stats.scoredOnes}/{stats.scoredCount} scored
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

function computeStats(blocks: StudioBlock[]) {
  let completed = 0,
    scheduled = 0,
    pending = 0,
    overdue = 0,
    scoreDue = 0,
    scoredOnes = 0,
    scoredCount = 0
  for (const b of blocks) {
    if (b.status === 'completed') completed++
    else if (b.status === 'scheduled') scheduled++
    else if (b.status === 'pending') pending++
    else if (b.status === 'overdue') overdue++
    if (b.score_due) scoreDue++
    if (b.score === 0 || b.score === 1) {
      scoredCount++
      if (b.score === 1) scoredOnes++
    }
  }
  return {
    total: blocks.length,
    completed,
    scheduled,
    pending,
    overdue,
    scoreDue,
    scoredOnes,
    scoredCount,
  }
}

// ─── Sub-components ────────────────────────────────────────────────────────


function Row({
  label, active, onClick, color, icon,
}: {
  label: string
  active: boolean
  onClick: () => void
  color?: string | null
  icon?: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'w-full text-left px-2 py-1.5 rounded text-sm font-light flex items-center gap-2 transition ' +
        (active
          ? 'bg-neutral-800 text-neutral-100'
          : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200')
      }
    >
      {color !== undefined && (
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: color || '#666' }}
        />
      )}
      {icon}
      <span className="truncate">{label}</span>
    </button>
  )
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="border border-neutral-800 rounded px-2 py-1.5">
      <div className={'text-lg font-light ' + (accent || 'text-neutral-100')}>{value}</div>
      <div className="text-[9px] uppercase tracking-wide text-neutral-500 mt-0.5">{label}</div>
    </div>
  )
}

// ─── Block detail view ────────────────────────────────────────────────────


function BlockDetailPanel({ blockId, onClose }: { blockId: string; onClose: () => void }) {
  const [block, setBlock] = useState<StudioBlock | null>(null)
  const [sections, setSections] = useState<StudioBlockSection[]>([])
  const [cards, setCards] = useState<StudioCard[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      getBlock(blockId),
      listBlockSections(blockId),
      listBlockCards(blockId),
    ])
      .then(([b, s, c]) => {
        if (cancelled) return
        setBlock(b)
        setSections(s)
        setCards(c)
      })
      .catch((e) => console.error('[studio] block detail load failed', e))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [blockId])

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-neutral-800 flex items-center gap-2">
        <button
          onClick={onClose}
          className="w-6 h-6 rounded hover:bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-neutral-100"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <h2 className="text-sm font-light text-neutral-200">Block</h2>
      </div>

      {loading || !block ? (
        <div className="p-4 text-xs text-neutral-500">Loading…</div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <div className="text-base font-light text-neutral-100 leading-snug">
              {block.title}
            </div>
            <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-wide text-neutral-500">
              <span>{block.block_type}</span>
              <span>·</span>
              <span>{block.status}</span>
              {block.score === 0 && <span className="text-red-400">· score 0</span>}
              {block.score === 1 && <span className="text-emerald-400">· score 1</span>}
              {block.fast && <span>· ⚡ fast</span>}
            </div>
          </div>

          {block.starts_at && (
            <div className="text-xs text-neutral-400 font-light">
              {format(new Date(block.starts_at), 'EEE MMM d, HH:mm')}
              {block.duration_minutes ? ` · ${block.duration_minutes} min` : ''}
            </div>
          )}

          {block.output_intent && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">
                Output intent
              </div>
              <div className="text-xs text-neutral-200 leading-relaxed whitespace-pre-wrap">
                {block.output_intent}
              </div>
            </div>
          )}

          {block.body_md && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">
                Body
              </div>
              <div className="text-xs text-neutral-200 leading-relaxed whitespace-pre-wrap">
                {block.body_md}
              </div>
            </div>
          )}

          {sections.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-2">
                Sections
              </div>
              <div className="space-y-2">
                {sections.map((s) => (
                  <div key={s.id} className="border border-neutral-800 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wide text-neutral-500 mb-1">
                      {s.kind}
                    </div>
                    <div className="text-xs text-neutral-200 leading-relaxed whitespace-pre-wrap">
                      {s.content_md}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cards.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-2">
                Cards ({cards.length})
              </div>
              <div className="space-y-1">
                {cards.map((c) => (
                  <div key={c.id} className="border border-neutral-800 rounded px-2 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-wide text-neutral-500">
                        {c.kind}
                      </span>
                      {c.title && <span className="text-neutral-200 truncate">{c.title}</span>}
                    </div>
                    {c.content_md && (
                      <div className="text-neutral-400 mt-1 line-clamp-3">{c.content_md}</div>
                    )}
                    {c.external_url && (
                      <a
                        href={c.external_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-400 hover:underline mt-1 inline-block"
                      >
                        {c.external_url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
